/**
 * Core requisition lifecycle state, as the API serialises it.
 *
 * DRAFT is a sync state, not a pipeline stage: raised in TeamForm, not yet
 * confirmed by the source system. FILLED and CLOSED are final. A source
 * system's own label (Advertising, Screening, Offer extended…) rides
 * separately on externalStatus and drives nothing.
 */
export const REQUISITION_STATES = [
  "DRAFT",
  "OPEN",
  "FILLED",
  "CLOSED",
] as const;

export type RequisitionState = (typeof REQUISITION_STATES)[number];

/** Still claiming capacity on the position. */
export const ACTIVE_REQUISITION_STATES: readonly RequisitionState[] = [
  "DRAFT",
  "OPEN",
];

export const isRequisitionState = (
  value: string | null | undefined
): value is RequisitionState =>
  Boolean(value) &&
  (REQUISITION_STATES as readonly string[]).includes(value as string);

/**
 * Label for display. GraphQL enum values are SHOUTED; a source system's own
 * status is not, and is shown exactly as it was given to us.
 */
export const formatRequisitionState = (
  value: string | null | undefined
): string => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return "";
  }
  if (!isRequisitionState(trimmed)) {
    return trimmed;
  }
  return trimmed.charAt(0) + trimmed.slice(1).toLowerCase();
};
